import type { IrisFinding } from "./iris-scan";
import type { IrisAction, IrisNote } from "./iris-watch";

/** Where the maker is in the studio, so Iris can guide them from the right place. */
export type GuidePage =
  | "home"
  | "library"
  | "cast-library"
  | "showcase"
  | "project"
  | "visual"
  | "background"
  | "storyboard"
  | "lyrics"
  | "render"
  | "release"
  | "other";

export interface IrisContext {
  page: GuidePage;
  projectId: string;
  projectTitle: string;
  /** Opens a step of the current production; set by the studio shell. */
  openStep: (step: string) => void;
  /** What Iris sees on this step right now (free, from the production's status). */
  note: IrisNote | null;
  /** What Iris found when she checked the film (free, from the production's status). */
  findings: IrisFinding[];
  /** Runs a studio action for a chip; set by the studio shell, with its own confirmations. An accepted proposal
   * passes the price the maker agreed to, so the page does not ask again (#99). */
  run: (action: IrisAction, options?: { confirmed?: boolean; priceUsd?: number | null }) => Promise<void> | void;
}

let context: IrisContext = { page: "home", projectId: "", projectTitle: "", openStep: () => {}, note: null, findings: [], run: () => {} };
const listeners = new Set<() => void>();

export function setIrisContext(update: Partial<IrisContext>): void {
  const next = { ...context, ...update };
  if (
    next.page === context.page &&
    next.projectId === context.projectId &&
    next.projectTitle === context.projectTitle &&
    next.openStep === context.openStep &&
    next.run === context.run &&
    (next.note?.key || "") === (context.note?.key || "") &&
    findingsKey(next.findings) === findingsKey(context.findings)
  )
    return;
  context = next;
  for (const listener of listeners) listener();
}

function findingsKey(findings: IrisFinding[]): string {
  return findings.map((finding) => `${finding.id}:${finding.title}`).join("|");
}

export function irisContext(): IrisContext {
  return context;
}

export function subscribeIrisContext(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The studio's own page names, as Iris understands them. */
export function guidePageFor(workspacePage: string): GuidePage {
  if (workspacePage === "dashboard") return "home";
  if (workspacePage === "lyrics") return "library";
  if (workspacePage === "cast-assets") return "cast-library";
  if (workspacePage === "landing") return "showcase";
  return "other";
}
